import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { Phone, ArrowRight, ShieldCheck, Clock, Award, Sparkles, ClipboardCheck, MessageSquare, Brush, Droplets } from "lucide-react";
import { site, images, enabledServices, portfolio, areas, globalFaqs, processSteps } from "@/config/site";
import { SEO, localBusinessLd } from "@/lib/seo";
import { Reveal } from "@/components/Reveal";
import { CTA } from "@/components/CTA";
import { Marquee } from "@/components/Marquee";
import { ServiceCard } from "@/components/ServiceCard";
import { Gallery } from "@/components/Gallery";
import { FAQAccordion } from "@/components/FAQAccordion";
import { trackEvent } from "@/lib/analytics";

const whyPoints = [
  { icon: ShieldCheck, title: "Fully insured", text: "Every project is covered, so you can book with confidence." },
  { icon: Award, title: "7 years in the trade", text: "Real experience across interiors, exteriors, cabinets and detail work." },
  { icon: Brush, title: "Preparation-first workmanship", text: "Lasting finishes are built on washing, sanding, filling and priming — never skipped." },
  { icon: Droplets, title: "Cleanliness & protection", text: "Floors, furniture and landscaping protected; work areas tidied daily." },
  { icon: ClipboardCheck, title: "Written estimates", text: "Clear scope, preparation and pricing in writing before work begins." },
  { icon: MessageSquare, title: "Straightforward communication", text: "You always know what happens next, and when." },
];

const EASE = [0.22, 1, 0.36, 1];

function FadeUp({ children, delay = 0, reduce, className = "" }) {
  return (
    <motion.div
      className={className}
      initial={reduce ? false : { opacity: 0, y: 26 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

export default function Home() {
  const heroRef = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const glowY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);

  return (
    <>
      <SEO
        title="North Ember Painting | Professional Painters in Delta, BC"
        description="North Ember Painting provides professional interior, exterior, cabinet and commercial painting in Delta, Ladner, Tsawwassen and Surrey, BC. Fully insured with 7 years of experience. Get a free estimate."
        path="/"
        jsonLd={[localBusinessLd()]}
      />

      {/* HERO — masked line reveal + parallax depth */}
      <section ref={heroRef} className="relative flex min-h-[calc(100svh-72px)] items-center overflow-hidden bg-ink texture-grain" data-testid="home-hero">
        <motion.div className="absolute inset-0" style={reduce ? undefined : { y: imgY }}>
          <motion.img
            src={images.hero.url}
            alt={images.hero.alt}
            fetchPriority="high"
            className="h-[115%] w-full object-cover"
            initial={reduce ? false : { scale: 1.14, opacity: 0.35 }}
            animate={{ scale: 1.02, opacity: 1 }}
            transition={{ duration: 2.1, ease: EASE }}
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/80 to-slate-900/30" aria-hidden="true" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-slate-950 to-transparent" aria-hidden="true" />
        <motion.div
          className="pointer-events-none absolute -right-24 top-16 h-[460px] w-[460px] rounded-full opacity-25 blur-3xl"
          style={{ background: "radial-gradient(circle, #F97316 0%, transparent 65%)", ...(reduce ? {} : { y: glowY }) }}
          aria-hidden="true"
        />
        <div className="relative mx-auto w-full max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <FadeUp delay={0.1} reduce={reduce}>
              <p className="inline-flex items-center gap-2 rounded-full border border-ember/40 bg-ember/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-ember">
                <Sparkles className="h-3.5 w-3.5" aria-hidden="true" /> Painters in Delta, BC
              </p>
            </FadeUp>
            <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.06] tracking-tight text-slate-50 sm:text-5xl lg:text-6xl">
              {["Professional Painting.", "Beautifully Finished."].map((line, i) => (
                <span key={line} className="block overflow-hidden pb-1">
                  <motion.span
                    className="block"
                    initial={reduce ? false : { y: "112%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 1.05, delay: 0.28 + i * 0.15, ease: EASE }}
                  >
                    {i === 1 ? (
                      <span
                        className="text-transparent"
                        style={{ backgroundImage: "linear-gradient(90deg,#F59E0B,#F97316)", backgroundClip: "text", WebkitBackgroundClip: "text" }}
                      >
                        {line}
                      </span>
                    ) : (
                      line
                    )}
                  </motion.span>
                </span>
              ))}
            </h1>
            <FadeUp delay={0.62} reduce={reduce}>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
                North Ember Painting provides professional interior, exterior, cabinet and commercial painting in
                Delta, BC and nearby communities — with careful preparation and finishes built to last.
              </p>
            </FadeUp>
            <FadeUp delay={0.78} reduce={reduce}>
              <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                <motion.span whileTap={reduce ? undefined : { scale: 0.97 }} className="inline-flex">
                  <Link
                    to="/estimate"
                    data-testid="hero-estimate-btn"
                    onClick={() => trackEvent("cta_estimate_click", { location: "hero" })}
                    className="inline-flex min-h-[52px] items-center justify-center gap-2 rounded-full bg-ember px-8 py-3.5 text-base font-semibold text-white shadow-xl shadow-orange-900/40 transition-[background-color,transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:bg-ember-dark"
                  >
                    Get a Free Estimate <ArrowRight className="h-5 w-5" aria-hidden="true" />
                  </Link>
                </motion.span>
                <motion.span whileTap={reduce ? undefined : { scale: 0.97 }} className="inline-flex">
                  <a
                    href={site.phoneHref}
                    data-testid="hero-call-btn"
                    onClick={() => trackEvent("phone_click", { location: "hero" })}
                    className="inline-flex min-h-[52px] items-center justify-center gap-2 rounded-full border border-slate-500/70 px-8 py-3.5 text-base font-semibold text-slate-100 backdrop-blur-sm transition-[border-color,background-color,transform] duration-300 hover:-translate-y-0.5 hover:border-ember hover:bg-white/5"
                  >
                    <Phone className="h-5 w-5 text-ember" aria-hidden="true" /> Call Now
                  </a>
                </motion.span>
              </div>
            </FadeUp>
            <FadeUp delay={0.94} reduce={reduce}>
              <p className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-400">
                <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-ember" aria-hidden="true" /> Fully insured</span>
                <span className="inline-flex items-center gap-1.5"><Award className="h-4 w-4 text-ember" aria-hidden="true" /> {site.yearsExperience} years of experience</span>
                <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4 text-ember" aria-hidden="true" /> Call or message any time</span>
              </p>
            </FadeUp>
          </div>
        </div>
        <span className="absolute bottom-4 right-4 rounded-full bg-slate-950/60 px-3 py-1 text-[10px] font-medium uppercase tracking-widest text-slate-400 backdrop-blur-sm">
          Concept image
        </span>
      </section>

      <Marquee />

      {/* SERVICES */}
      <section className="py-20 md:py-28" data-testid="home-services">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-ember">What we do</p>
            <div className="mt-3 flex flex-wrap items-end justify-between gap-6">
              <h2 className="max-w-xl font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
                Painting services for every surface of your property
              </h2>
              <Link to="/services" data-testid="home-services-all" className="inline-flex items-center gap-1.5 text-sm font-semibold text-ember-dark transition-colors duration-200 hover:text-ember">
                View all services <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {enabledServices.slice(0, 6).map((s, i) => (
              <Reveal key={s.slug} delay={i * 70}>
                <ServiceCard service={s} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="bg-ink py-20 md:py-28 texture-grain" data-testid="home-why">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-ember">Why North Ember</p>
            <h2 className="mt-3 max-w-2xl font-display text-3xl font-bold tracking-tight text-slate-50 sm:text-4xl lg:text-5xl">
              A painting company built on doing the basics exceptionally well
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {whyPoints.map((p, i) => (
              <Reveal key={p.title} delay={i * 60}>
                <div className="tilt-card h-full rounded-2xl border border-white/10 bg-white/[0.04] p-7 transition-[transform,border-color,background-color] duration-300 hover:-translate-y-1 hover:border-ember/40 hover:bg-white/[0.07]">
                  <p.icon className="h-6 w-6 text-ember" aria-hidden="true" />
                  <h3 className="mt-4 font-display text-lg font-semibold text-slate-50">{p.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS PREVIEW */}
      <section className="py-20 md:py-28" data-testid="home-process">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-ember">How it works</p>
            <h2 className="mt-3 max-w-2xl font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
              A clear process from first call to final walkthrough
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {processSteps.slice(0, 4).map((step, i) => (
              <Reveal key={step.title} delay={i * 70}>
                <div className="h-full rounded-2xl border border-slate-200 bg-white p-7 shadow-sm">
                  <span className="font-display text-4xl font-extrabold text-ember/25">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-3 font-display text-lg font-semibold text-slate-900">{step.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{step.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-8">
            <Link to="/process" data-testid="home-process-link" className="inline-flex items-center gap-1.5 text-sm font-semibold text-ember-dark transition-colors duration-200 hover:text-ember">
              See the full 8-step process <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* PROJECTS PREVIEW */}
      <section className="bg-slate-100 py-20 md:py-28" data-testid="home-projects">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-ember">Our projects</p>
            <div className="mt-3 flex flex-wrap items-end justify-between gap-6">
              <h2 className="max-w-xl font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
                Finishes we're proud to put our name on
              </h2>
              <Link to="/projects" data-testid="home-projects-all" className="inline-flex items-center gap-1.5 text-sm font-semibold text-ember-dark transition-colors duration-200 hover:text-ember">
                Browse the gallery <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-slate-500">
              Our project photo library is being built as real jobs are completed. The images shown are clearly
              labelled concept images that represent the style and standard of our work.
            </p>
          </Reveal>
          <div className="mt-10">
            <Gallery items={portfolio.slice(0, 6)} showFilters={false} />
          </div>
        </div>
      </section>

      {/* COLOUR TEASER */}
      <section className="py-20 md:py-28" data-testid="home-colours">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <Reveal>
            <div className="overflow-hidden rounded-2xl shadow-xl">
              <img src={images.swatchDeck.url} alt={images.swatchDeck.alt} loading="lazy" className="aspect-[4/3] w-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={120}>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-ember">Colour palette tool</p>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Explore colours before a single wall is painted
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              Try our interactive palette selector: coordinated light, neutral and bold palettes with a live room
              preview. Then confirm your favourites with a real paint sample — screens never tell the whole story.
            </p>
            <Link
              to="/colours"
              data-testid="home-colours-btn"
              className="mt-7 inline-flex min-h-[48px] items-center gap-2 rounded-full border-2 border-slate-900 px-6 py-2.5 text-sm font-semibold text-slate-900 transition-[background-color,color,border-color] duration-300 hover:bg-slate-900 hover:text-white"
            >
              Open the Colour Tool <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* SERVICE AREAS */}
      <section className="bg-ink py-20 md:py-28 texture-grain" data-testid="home-areas">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-ember">Where we work</p>
            <h2 className="mt-3 max-w-2xl font-display text-3xl font-bold tracking-tight text-slate-50 sm:text-4xl lg:text-5xl">
              Proudly painting across the South Fraser region
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {areas.map((a, i) => (
              <Reveal key={a.slug} delay={i * 70}>
                <Link
                  to={`/service-areas/${a.slug}`}
                  data-testid={`home-area-${a.slug}`}
                  className="tilt-card group block h-full overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-ember/40"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={images[a.image].url} alt={images[a.image].alt} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div className="p-5">
                    <h3 className="font-display text-lg font-semibold text-slate-50">{a.name}</h3>
                    <p className="mt-1 inline-flex items-center gap-1 text-sm font-medium text-ember">
                      Painting services <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                    </p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ PREVIEW */}
      <section className="py-20 md:py-28" data-testid="home-faq">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-ember">Common questions</p>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Answers, before you even ask
            </h2>
          </Reveal>
          <Reveal className="mt-8">
            <FAQAccordion faqs={globalFaqs.slice(0, 4)} idPrefix="home-faq" />
          </Reveal>
          <Reveal className="mt-6">
            <Link to="/faqs" data-testid="home-faq-all" className="inline-flex items-center gap-1.5 text-sm font-semibold text-ember-dark transition-colors duration-200 hover:text-ember">
              Read all FAQs <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
      </section>

      <CTA />
    </>
  );
}
